import React, { useState } from 'react';
import { useEffect } from 'react';
import { PricingCard } from './PricingCard';
import { SUBSCRIPTION_PLANS } from '../../utils/stripe/config';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../utils/supabase';

export interface CurrentSubscriptionData {
  id: string | null;
  planId: string | null;
  currentPeriodStart?: number | null;
  currentPeriodEnd?: number | null;
  cancelAtPeriodEnd?: boolean | null;
  status?: string | null;
}

export function PricingPlans() {
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSubscriptionData, setCurrentSubscriptionData] = useState<CurrentSubscriptionData | null>(null);
  const user = useAuthStore(state => state.user);
  const [nextPlan, setNextPlan] = useState<string | null>(null);
  
  // ユーザーの購読情報を取得
  useEffect(() => {
    if (!user?.id) {
      setSelectedPlanId(null);
      setNextPlan(null);
      setCurrentSubscriptionData(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchSubscription = async () => {
      try {
        setIsLoading(true);

        const { data: stripe_customer, error: customerError } = await supabase
          .from('stripe_customers')
          .select('*')
          .eq('user_id', user.id)
          .maybeSingle();

        if (customerError) throw customerError;

        const { data: userData, error: userError } = await supabase
          .from('users')
          .select('plan, next_plan')
          .eq('id', user.id)
          .single();

        if (userError) {
          console.error('Failed to fetch user plan:', userError);
        }

        const planFromUser =
          userData?.plan && userData.plan !== 'unsubscribed' ? userData.plan : null;
        const pendingPlan = userData?.next_plan || null;

        if (!stripe_customer) {
          if (!cancelled) {
            setSelectedPlanId(planFromUser);
            setNextPlan(null);
            setCurrentSubscriptionData(null);
          }
          return;
        }

        const { data: stripeSubscription, error: stripeSubscriptionErr } = await supabase
          .from('stripe_subscriptions')
          .select('*')
          .eq('customer_id', stripe_customer.customer_id)
          .maybeSingle();

        if (stripeSubscriptionErr) throw stripeSubscriptionErr;

        if (stripeSubscription) {
          const planFromStripe = SUBSCRIPTION_PLANS.find(
            (p) => p.priceId === stripeSubscription.price_id
          );
          const resolvedPlanId = planFromUser ?? planFromStripe?.id ?? null;
          const isActive = stripeSubscription.status === 'active';

          if (!cancelled) {
            setCurrentSubscriptionData({
              id: stripeSubscription.subscription_id,
              planId: resolvedPlanId,
              currentPeriodStart: stripeSubscription.current_period_start,
              currentPeriodEnd: stripeSubscription.current_period_end,
              cancelAtPeriodEnd: stripeSubscription.cancel_at_period_end,
              status: stripeSubscription.status,
            });

            setSelectedPlanId(resolvedPlanId);
            setNextPlan(
              isActive && pendingPlan && pendingPlan !== resolvedPlanId ? pendingPlan : null
            );
          }
        } else if (!cancelled) {
          setSelectedPlanId(planFromUser);
          setNextPlan(null);
          setCurrentSubscriptionData(null);
        }
      } catch (err) {
        console.error('Failed to fetch subscription:', err);
        if (!cancelled) {
          setSelectedPlanId(null);
          setNextPlan(null);
          setCurrentSubscriptionData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchSubscription();

    return () => {
      cancelled = true;
    };
  }, [user?.id]);

  // 表示するプランをフィルタリング（未加入プランを除外）
  const displayPlans = SUBSCRIPTION_PLANS.filter(plan => plan.id !== 'unsubscribed');
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
      {displayPlans.map((plan) => (
        <PricingCard
          key={plan.id}
          plan={plan}
          currentSubscriptionData={currentSubscriptionData}
          isNext = { nextPlan === plan.id}
          isSelected={selectedPlanId === plan.id}
          isExistedNext={!!nextPlan}
          isLoading={isLoading}
          isDisabled={currentSubscriptionData?.cancelAtPeriodEnd ?? false}
        />
      ))}
    </div>
  );
}